'use client';

import { useTranslations } from 'next-intl';
import { History } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import type { RepairCycleStatus, ScanBatch } from '../types';

// Same styling as the Oman Repair feature's STATUS_STYLES map, so repair
// terminology/colors are consistent app-wide (see
// `repairs/components/oman-repair-listing.tsx`).
export const REPAIR_STATUS_STYLES: Record<RepairCycleStatus, string> = {
  waiting:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  completed:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  returned:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

interface RepairHistoryButtonProps {
  batch: ScanBatch;
  label: string;
}

// Icon-only button that opens a popover listing every repair cycle for a
// batch, newest first. Only meaningful when `batch.repair_count > 0` —
// callers are expected to gate rendering on that.
export function RepairHistoryButton({
  batch,
  label
}: RepairHistoryButtonProps) {
  const t = useTranslations('photoScanning');
  const tRepair = useTranslations('repairs');

  function getLabel(status: RepairCycleStatus): string {
    switch (status) {
      case 'waiting':
        return tRepair('statusWaiting');
      case 'completed':
        return tRepair('statusCompleted');
      case 'returned':
        return tRepair('statusReturned');
    }
  }

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size='icon'
              variant='ghost'
              className='text-muted-foreground hover:text-foreground h-8 w-8'
            >
              <History className='h-4 w-4' />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent align='end' className='w-80'>
        <div className='space-y-3'>
          <p className='text-sm font-medium'>{t('repairHistoryTitle')}</p>
          {batch.repair_history.length === 0 ? (
            <p className='text-muted-foreground text-sm'>
              {t('repairHistoryEmpty')}
            </p>
          ) : (
            <div className='max-h-80 space-y-3 overflow-y-auto'>
              {batch.repair_history.map((entry, index) => (
                <div
                  key={index}
                  className='space-y-1.5 border-b pb-3 text-sm last:border-b-0 last:pb-0'
                >
                  <div className='flex items-center justify-between gap-2'>
                    <Badge
                      variant='secondary'
                      className={cn(REPAIR_STATUS_STYLES[entry.repair_status])}
                    >
                      {getLabel(entry.repair_status)}
                    </Badge>
                    <span className='text-muted-foreground text-xs'>
                      {entry.transferred_at
                        ? new Date(entry.transferred_at).toLocaleDateString(
                            undefined,
                            { year: 'numeric', month: 'long', day: 'numeric' }
                          )
                        : '—'}
                    </span>
                  </div>
                  <p>
                    <span className='font-medium'>
                      {t('repairHistoryReason')}:
                    </span>{' '}
                    {entry.notes ?? '—'}
                  </p>
                  {entry.created_by?.name && (
                    <p className='text-muted-foreground text-xs'>
                      {t('sentBy')}{' '}
                      <span className='font-medium'>
                        {entry.created_by.name}
                      </span>
                    </p>
                  )}
                  <p>
                    <span className='font-medium'>
                      {t('repairHistoryComment')}:
                    </span>{' '}
                    {entry.repair_comment ?? (
                      <span className='text-muted-foreground'>
                        {t('repairHistoryNoComment')}
                      </span>
                    )}
                  </p>
                  {entry.confirmed_by?.name && (
                    <p className='text-muted-foreground text-xs'>
                      {t('confirmedBy')}{' '}
                      <span className='font-medium'>
                        {entry.confirmed_by.name}
                      </span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
